import { Badge, Button } from "@eduviaos/ui-kit";
import type { CapabilitySuggestion, SuggestionStatus } from "@eduviaos/shared";
import { History } from "lucide-react";

/**
 * Compact recall card for the most recent AI suggestion in the current
 * editor scope (Phase F-finale-4 Slice 5). Lets an author re-open the diff
 * for a suggestion they generated earlier in the session. `expired`
 * suggestions are purged server-side (TTL) so re-open is disabled.
 */
export interface LastSuggestionPanelProps {
  suggestion: CapabilitySuggestion | null;
  onReopen: () => void;
}

const STATUS_META: Record<
  SuggestionStatus,
  { label: string; variant: "info" | "success" | "danger" | "secondary" }
> = {
  awaiting_review: { label: "Awaiting review", variant: "info" },
  partially_applied: { label: "Partially applied", variant: "success" },
  rejected: { label: "Rejected", variant: "danger" },
  expired: { label: "Expired", variant: "secondary" },
};

export function LastSuggestionPanel({ suggestion, onReopen }: LastSuggestionPanelProps) {
  if (!suggestion) return null;

  const meta = STATUS_META[suggestion.status];
  const expired = suggestion.status === "expired";

  return (
    <div className="border-border bg-muted/30 flex items-center gap-3 rounded-md border p-3">
      <History className="text-muted-foreground size-4 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground text-[11px] font-medium uppercase tracking-wide">
            Last AI suggestion
          </span>
          <Badge variant={meta.variant}>{meta.label}</Badge>
        </div>
        <p className="text-foreground mt-0.5 truncate text-sm" title={suggestion.user_prompt}>
          {suggestion.user_prompt}
        </p>
      </div>
      <Button variant="outline" size="sm" onClick={onReopen} disabled={expired}>
        Review
      </Button>
    </div>
  );
}
